import {
  FC,
  SyntheticEvent,
  useEffect,
  useState,
  FocusEvent,
  useRef,
} from 'react';
import {
  Autocomplete,
  TextField,
  CircularProgress,
  ListItem,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { OptionType } from '../../utils/type';
import { FormattedMessage } from 'react-intl';

interface LazyAutocompleteSearchProps {
  labelId: string;
  defaultId: string;
  value: OptionType | null;
  placeholderOnfloatBackgroundColor?: string;
  onChange: (value: OptionType | null) => void;
  onBlur?: (event: FocusEvent<HTMLDivElement>) => void;
  onFocus?: (event: FocusEvent<HTMLDivElement>) => void;

  /** API function */
  fetchOptions: (search: string, page: number) => Promise<OptionType[]>;

  /** UI */
  disabled?: boolean;
  required?: boolean;
  pageSize?: number;
}

const LazyAutocompleteSearch: FC<LazyAutocompleteSearchProps> = ({
  labelId,
  defaultId,
  value,
  placeholderOnfloatBackgroundColor,
  onChange,
  onBlur,
  onFocus,
  fetchOptions,
  disabled,
  required,
  pageSize = 10,
}) => {
  const [options, setOptions] = useState<OptionType[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  /* ---------------- Debounce Search ---------------- */
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const paginationLock = useRef(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(inputValue);
      setPage(0);
      setHasMore(true);
    }, 400);

    return () => clearTimeout(timer);
  }, [inputValue]);

  /* ---------------- Fetch Data ---------------- */
  useEffect(() => {
    const abortController = new AbortController();

    const load = async () => {
      if (!hasMore && page !== 0) return;

      setLoading(true);
      try {
        const data = await fetchOptions(debouncedSearch, page);

        // Check if request was aborted
        if (abortController.signal.aborted) return;

        setOptions((prev) => {
          if (page === 0) return data;

          const existingIds = new Set(prev.map((item) => item.id));

          const newItems = data.filter((item) => !existingIds.has(item.id));

          return [...prev, ...newItems];
        });

        setHasMore(data.length >= pageSize);
      } catch (error) {
        // Don't log error if request was aborted
        if (!abortController.signal.aborted) {
          console.error('Error fetching options:', error);
        }
      } finally {
        if (!abortController.signal.aborted) {
          setLoading(false);
        }
        paginationLock.current = false;
      }
    };

    load();
    return () => {
      abortController.abort();
    };
  }, [debouncedSearch, page]);

  useEffect(() => {
    if (value) {
      setInputValue(value.label);
    }
  }, [value]);

  /* ---------------- Scroll Pagination ---------------- */
  const handleScroll = (event: SyntheticEvent) => {
    const listboxNode = event.currentTarget as HTMLElement;

    if (
      listboxNode.scrollTop + listboxNode.clientHeight >=
        listboxNode.scrollHeight - 50 &&
      !loading &&
      hasMore &&
      !paginationLock.current
    ) {
      paginationLock.current = true;
      setPage((prev) => prev + 1);
    }
  };

  return (
    <Autocomplete
      className="consentAutoSearch"
      sx={{
        width: '100%',
        height: '36px',
        display: 'flex',
        backgroundColor: 'white',
        '& .MuiInputLabel-root': {
          fontFamily: 'Lato-Regular',
          fontSize: '14px',
          paddingTop: '2px',
          color: '#97A6A5',
        },
        '& .MuiInput-root .MuiInput-input': {
          fontFamily: 'Lato-Regular',
          fontSize: '14px',
        },
        '& .MuiOutlinedInput-input': {
          padding: '7.5px 4px 7.5px 7px !important',
          fontFamily: 'Lato-Regular',
          fontSize: '14px',
          cursor: disabled ? 'not-allowed' : 'auto',
        },
        '& label.Mui-focused': {
          color: '#00C6B8',
          backgroundColor: placeholderOnfloatBackgroundColor ?? 'white',
        },
        '& .MuiOutlinedInput-root': {
          '&.Mui-focused fieldset': {
            borderColor: '#00C6B8',
            borderWidth: '1px',
          },
          '&:hover fieldset': {
            borderColor: '#00C6B8',
          },
        },
        '& .MuiAutocomplete-inputRoot': {
          height: '36px',
          padding: '2px 10px',
        },
        '& label.MuiFormLabel-filled': {
          backgroundColor: placeholderOnfloatBackgroundColor ?? 'white',
          color: '#97A6A5',
        },
        '& .MuiOutlinedInput-': {
          backgroundColor: placeholderOnfloatBackgroundColor ?? 'red',
        },
        // Disabled styling
        '& .MuiOutlinedInput-root.Mui-disabled': {
          backgroundColor: '#F2F2F2',
          cursor: 'not-allowed',
        },

        '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline':
          {
            borderColor: '#D0D0D0 !important',
          },

        '& .MuiOutlinedInput-root.Mui-disabled:hover .MuiOutlinedInput-notchedOutline':
          {
            borderColor: '#D0D0D0 !important',
          },

        '& .MuiOutlinedInput-root.Mui-disabled input': {
          cursor: 'not-allowed',
        },

        '& .MuiOutlinedInput-root.Mui-disabled .MuiAutocomplete-popupIndicator':
          {
            cursor: 'not-allowed',
          },
      }}
      getOptionDisabled={(option) => option.id === 'loading'}
      disabled={disabled}
      options={options}
      value={value}
      filterOptions={(opts) => {
        const updated = [...opts];

        if (loading) {
          updated.push({
            id: 'loading',
            label: 'Loading...',
          });
        }

        return updated;
      }}
      loading={loading}
      onBlur={onBlur}
      onFocus={onFocus}
      onKeyDown={(event) => {
        if ((event.code === 'Backspace' || event.code === 'Delete') && value) {
          setInputValue(value.label);
          onChange(null);
        }
      }}
      inputValue={inputValue}
      onInputChange={(_, newValue) => {
        setInputValue(newValue);
      }}
      onChange={(_, newValue) => onChange(newValue)}
      isOptionEqualToValue={(opt, val) => opt.id === val.id}
      getOptionLabel={(option) => option.label}
      ListboxProps={{
        sx: {
          fontFamily: 'Lato-Regular',
          fontSize: '14px',
          wordBreak: 'break-word',

          maxHeight: '250px',
          overflowY: 'auto',

          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#CFDEDE',
            borderRadius: '4px',
          },
        },
        onScroll: handleScroll,
      }}
      popupIcon={
        <KeyboardArrowDownIcon
          style={{
            color: '#97a6a5',
          }}
        />
      }
      renderOption={(props, option) => {
        if (option.id === 'loading') {
          return (
            <ListItem
              {...props}
              key="loading"
              style={{ justifyContent: 'center' }}
            >
              <CircularProgress size={16} color="primary" />
            </ListItem>
          );
        }

        return (
          <ListItem {...props} key={option.id}>
            {option.label}
          </ListItem>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={<FormattedMessage id={labelId} defaultMessage={defaultId} />}
          className="inputSelectFormLabel"
          sx={{
            height: '36px',
            padding: '0px',
          }}
          required={required}
        />
      )}
    />
  );
};

export default LazyAutocompleteSearch;
